import React, {
  useState, useRef, useEffect, useContext,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { getBrandingColor, colorTypes } from 'src/branding';

/**
 * Based on https://medium.com/javascript-in-plain-english/react-custom-toast-notification-component-from-scratch-adccd1c452b8
 *
 * Usage:
 *
 * in top/parent component, eg App:
 *
 * withToaster(App)
 *
 * where the toast is to be displayed:
 * const toast = useToast();

 * messages: toast.addMessage("This is a message")
 * error messages: toast.addError("This is a error", customDuration=1300)
 */

const ToastContext = React.createContext(null);
export default ToastContext;

// Create a random ID
const generateUEID = () => {
  let first = (Math.random() * 46656) | 0;
  let second = (Math.random() * 46656) | 0;
  first = (`000${first.toString(36)}`).slice(-3);
  second = (`000${second.toString(36)}`).slice(-3);

  return first + second;
};

export const withToastProvider = (Component) => (props) => {
  const [toasts, setToasts] = useState([]);

  const addMessage = (message, customDuration = 2500) => {
    let id = '';
    do {
      id = generateUEID();
    } while (toasts.map((t) => t.id).indexOf(id) >= 0);
    setToasts((toasts) => [
      ...toasts,
      {
        id, message, duration: customDuration, kind: 'info',
      },
    ]);
  };

  const addError = (message, customDuration = 4000) => {
    const id = generateUEID();

    setToasts([
      ...toasts,
      {
        id, message, duration: customDuration, kind: 'error',
      },
    ]);
  };

  const remove = (id) => setToasts(toasts.filter((t) => t.id !== id));

  return (
    <ToastContext.Provider value={{ addMessage, addError }}>
      <Component {...props} />

      {createPortal(
        <div id="toasts-wrapper">
          {toasts.map(({
            id, message, kind, duration,
          }) => (
            <Toast
              key={id}
              message={message}
              kind={kind}
              duration={duration}
              remove={() => remove(id)}
            />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

const ToastWrapper = styled(motion.div)`
  border: 1px solid
    ${({ kind }) => (kind === 'info' ? getBrandingColor(colorTypes.primary) : getBrandingColor(colorTypes.error))};
  border-radius: 15px;
  box-shadow: 0 0 10px 4px rgba(0, 0, 0, 0.19);
  z-index: 2;
  margin: 1rem;
  background: white;
  position: relative;

  width: fit-content;
  left: 50%;
  transform: translateX(-50%);

  @media screen and (max-width: 500px) {
    width: 90%;
  }
`;

const toastVariants = {
  enter: {
    transform: 'translateY(0%) translateX(-50%)',
    left: '50%',
  },
  exit: {
    transform: 'translateY(-300%) translateX(-50%)',
    left: '50%',
    transition: {
      duration: 0.6,
    },
  },
};

const ToastContent = styled.p`
  line-height: 1.75;
  text-align: center;
  margin: 0;
  padding: 0.8rem 3rem;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.2rem;
  right: 0.2rem;
  background: none;
  font-size: 1.1rem;
  color: gray;
  border: none;
  :hover {
    cursor: pointer;
  }

  :active,
  :focus,
  :focus:active {
    color: #444444;
    outline: none;
  }
`;

const Toast = ({
  message = '', kind, remove, duration,
}) => {
  const removeRef = useRef();
  removeRef.current = remove;
  const [show, setShow] = useState(false);
  const fadeOutDuration = 300;

  useEffect(() => {
    const tSlideUp = setTimeout(() => setShow(false), duration);

    // delay before fading in, so that the animation gets a chance to finish before the next appears
    // this prevents some glitches from occuring
    const tSlideDown = setTimeout(() => setShow(true), 200);
    const tRemove = setTimeout(
      () => removeRef.current(),
      duration + fadeOutDuration + 100,
    );

    return () => {
      clearTimeout(tSlideUp);
      clearTimeout(tRemove);
      clearTimeout(tSlideDown);
    };
  }, [duration]);

  return (
    <AnimatePresence>
      {show && (
        <ToastWrapper
          initial={toastVariants.exit}
          animate={toastVariants.enter}
          exit={toastVariants.exit}
          role="modal"
          kind={kind}
        >
          <ToastContent>{message}</ToastContent>
          <CloseBtn
            aria-label="Lukk melding"
            onClick={() => {
              removeRef.current();
            }}
          >
            <span arial-hidden="true">X</span>
          </CloseBtn>
        </ToastWrapper>
      )}
    </AnimatePresence>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  return { addMessage: context.addMessage, addError: context.addError };
};
